/**
 * Pay now on a portal invoice (tracker item 45, phase 3), through the same
 * processor card form as the public link (features/online-payments) and the
 * portal pay endpoints. The card number never reaches this app: the form
 * returns a one-time token.
 *
 * Retry safety: the backend's idempotency key follows the card token. After a
 * failure whose outcome we do not know (no answer, a server error, a 429) the
 * token is KEPT and "Try the same payment again" resends the very same token
 * and amount, so a customer can never be charged twice; "Use a different card"
 * drops it. A declined card or any other refusal clears it (a new card entry
 * makes a new token). A PROCESSING answer says not to pay again.
 *
 * Saved cards are listed for information (and managed under Payment methods):
 * the portal pay endpoint takes a card form token only, never a saved card.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { createAPIClient, IPortalApi } from '../../api';
import * as types from '../../api/types';
import { usePortal } from './PortalContext';
import { InlineAlert } from './PortalStates';
import { usePaymentForm } from '../../features/online-payments/usePaymentForm';
import { PaymentFormError } from '../../features/online-payments/adapters/paymentFormAdapter';
import { amountError, cardLabel, formatUsd } from '../../features/online-payments/onlinePaymentsUi';
import { inputClass, labelClass, primaryButtonClass, secondaryButtonClass } from '../../components/formStyles';
import { canRetryWithSameToken, describePortalFailure } from '../../utils/portal';

interface Props {
  invoiceId: string;
  invoiceNumber: string;
  balanceDue: string;
  /** Open the form straight away (the dashboard's "Pay invoice" link) */
  openOnLoad?: boolean;
  /** A payment went through: the page reloads its totals */
  onPaid?: () => void;
}

interface PendingPayment {
  sourceToken: string;
  amount: string;
  saveCard: boolean;
  autopay: boolean;
}

export function PortalPayPanel({ invoiceId, invoiceNumber, balanceDue, openOnLoad = false, onPaid }: Props) {
  const api = useMemo(() => createAPIClient() as IPortalApi, []);
  const { reportFailure } = usePortal();
  const [config, setConfig] = useState<types.PublicPayConfig | null>(null);
  const [configFailure, setConfigFailure] = useState<string | null>(null);
  const [cards, setCards] = useState<types.PortalPaymentMethod[]>([]);
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState('');
  const [saveCard, setSaveCard] = useState(false);
  const [autopay, setAutopay] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<PendingPayment | null>(null);
  const [result, setResult] = useState<types.PublicPayResult | null>(null);
  const hasBalance = Number(balanceDue) > 0;
  const form = usePaymentForm(open && config?.available ? config.clientConfig : null);

  useEffect(() => {
    if (!hasBalance) return undefined;
    let active = true;
    api.getPortalPayConfig(invoiceId).then(
      (c) => {
        if (!active) return;
        setConfig(c);
        setAmount(c.balanceDue);
        if (openOnLoad && c.available) setOpen(true);
      },
      (cause) => {
        if (!active) return;
        const described = describePortalFailure(cause, 'Online payment could not be checked right now.');
        setConfigFailure(described.message);
        reportFailure(described);
      },
    );
    api.getPortalPaymentMethods().then(
      (c) => { if (active) setCards(c.methods.filter((m) => m.status === 'ACTIVE')); },
      () => { /* the card list is informational */ },
    );
    return () => { active = false; };
    // the amount is reset only when the invoice changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, invoiceId]);

  // A payment that went through: show its receipt even when the balance is now 0
  if (result && result.status !== 'DECLINED') {
    const paid = result.status === 'PAID';
    return (
      <section aria-labelledby="pay-result-heading" className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200 space-y-2" data-testid="pay-result">
        <h2 id="pay-result-heading" className={`text-lg font-semibold ${paid ? 'text-green-800' : 'text-amber-800'}`}>
          {paid ? `Payment of ${formatUsd(result.amount)} received` : 'Payment being confirmed'}
        </h2>
        <p className="text-sm text-gray-800">{result.message}</p>
        {paid && result.card && <p className="text-sm text-gray-700">Paid with {cardLabel(result.card)}.</p>}
        {paid && result.cardSaved && <p className="text-sm text-gray-700">Your card is saved{result.autopayEnabled ? ' and will pay future invoices automatically' : ''}.</p>}
        {paid && result.receiptUrl && (
          <a href={result.receiptUrl} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-[44px] items-center text-sm font-semibold text-blue-700 hover:underline">View card receipt</a>
        )}
        {paid && Number(result.balanceDue) > 0 && <p className="text-sm text-gray-700">Remaining balance: {formatUsd(result.balanceDue)}.</p>}
        {!paid && <p className="text-sm font-semibold text-amber-900">Please do not pay again. Check your email or this page later for the receipt.</p>}
      </section>
    );
  }

  if (!hasBalance) return null;
  if (configFailure) return <InlineAlert>{configFailure}</InlineAlert>;
  if (!config) return null;
  if (!config.available) {
    return config.unavailableReason ? <p className="text-sm text-gray-600" data-testid="pay-unavailable">{config.unavailableReason}</p> : null;
  }

  const amountProblem = amountError(amount, config.minAmount, config.balanceDue);
  const locked = busy || !!pending;

  async function pay(event: React.FormEvent) {
    event.preventDefault();
    if (busy || !config) return;
    if (!pending && amountProblem) return;
    setBusy(true);
    setError(null);
    let attempt = pending;
    try {
      if (!attempt) {
        const { sourceToken } = await form.tokenize({ amount: Number(amount).toFixed(2), intent: 'CHARGE' });
        attempt = { sourceToken, amount: Number(amount).toFixed(2), saveCard, autopay: saveCard && autopay };
      }
      const consent = config.autopayConsent;
      const answer = await api.payPortalInvoice(invoiceId, {
        sourceId: attempt.sourceToken,
        amount: attempt.amount,
        saveCard: attempt.saveCard || undefined,
        autopayConsent: attempt.autopay && consent ? { accepted: true, version: consent.version } : undefined,
      });
      setPending(null); // the token is used: a new card entry makes a new one
      setResult(answer);
      if (answer.status === 'DECLINED') setError(answer.message);
      if (answer.status === 'PAID') onPaid?.();
    } catch (cause) {
      if (cause instanceof PaymentFormError) {
        setError(cause.message);
      } else if (attempt && canRetryWithSameToken(cause)) {
        // Outcome unknown: keep the token so the retry cannot charge twice
        setPending(attempt);
        const described = describePortalFailure(cause, 'The payment could not be sent.');
        setError(
          described.kind === 'rate-limit'
            ? `${described.message} Your card was not charged. You can try the same payment again in a few minutes.`
            : 'We could not confirm whether your payment went through. Try the same payment again: it is safe, you will not be charged twice.',
        );
      } else {
        setPending(null);
        const described = describePortalFailure(cause, 'The payment could not be sent. Please try again.');
        setError(described.message);
        reportFailure(described);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <section aria-labelledby="pay-heading" className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200">
      {!open ? (
        <button type="button" className={`${primaryButtonClass} w-full text-base sm:w-auto`} onClick={() => setOpen(true)}>Pay now</button>
      ) : (
        <form onSubmit={(e) => void pay(e)} className="max-w-md space-y-4" aria-labelledby="pay-heading" noValidate>
          <h2 id="pay-heading" className="text-lg font-semibold">Pay invoice {invoiceNumber}</h2>
          {cards.length > 0 && (
            <p className="text-sm text-gray-600" data-testid="cards-on-file">
              Cards on file: {cards.map((c) => `${cardLabel(c)}${c.autopayEnabled ? ' (autopay)' : ''}`).join(', ')}.{' '}
              <Link to="/portal/payment-methods" className="font-semibold text-blue-700 hover:underline">Manage cards</Link>. This payment uses the card you enter below.
            </p>
          )}
          <div>
            <label htmlFor="pay-amount" className={labelClass}>Amount (USD)</label>
            <input
              id="pay-amount" inputMode="decimal" className={`${inputClass} min-h-[44px] text-base`} value={amount} disabled={locked}
              onChange={(e) => setAmount(e.target.value)} aria-invalid={!!amountProblem && !pending} aria-describedby="pay-amount-hint"
            />
            <p id="pay-amount-hint" className={`mt-1 text-xs ${amountProblem && !pending ? 'text-red-700' : 'text-gray-500'}`}>
              {amountProblem && !pending ? amountProblem : `Balance due ${formatUsd(config.balanceDue)}${Number(config.minAmount) < Number(config.balanceDue) ? `; at least ${formatUsd(config.minAmount)}` : ''}.`}
            </p>
          </div>
          <div hidden={!!pending}>
            <span className={labelClass}>Card</span>
            <div ref={form.containerRef} data-testid="card-form" className="min-h-[3rem]" />
            {form.status === 'loading' && <p className="text-sm text-gray-500">Loading the secure card form…</p>}
            {form.error && <InlineAlert>{form.error}</InlineAlert>}
          </div>
          {pending && (
            <div role="status" className="rounded-lg bg-amber-50 p-3 text-sm text-amber-900 ring-1 ring-amber-200" data-testid="pay-pending">
              Your payment of {formatUsd(pending.amount)} is waiting to be sent again. It uses the card you already entered.
            </div>
          )}
          {config.cardsOnFile && !pending && (
            <div className="space-y-2 text-sm">
              <label className="flex min-h-[44px] items-start gap-3">
                <input type="checkbox" className="mt-0.5 h-5 w-5" checked={saveCard} disabled={locked}
                  onChange={(e) => { setSaveCard(e.target.checked); if (!e.target.checked) setAutopay(false); }} />
                <span>Save this card for future payments</span>
              </label>
              {config.autopayConsent && (
                <label className="flex min-h-[44px] items-start gap-3">
                  <input type="checkbox" className="mt-0.5 h-5 w-5" checked={autopay} disabled={locked || !saveCard}
                    onChange={(e) => setAutopay(e.target.checked)} aria-describedby="autopay-consent-text" />
                  <span>
                    <span className="font-medium">Pay future invoices automatically</span>
                    <span id="autopay-consent-text" className="block text-xs text-gray-600" data-consent-version={config.autopayConsent.version}>
                      {config.autopayConsent.text}
                    </span>
                  </span>
                </label>
              )}
            </div>
          )}
          {error && <InlineAlert>{error}</InlineAlert>}
          <div className="flex flex-col-reverse gap-3 sm:flex-row">
            {pending ? (
              <button type="button" className={secondaryButtonClass} disabled={busy} onClick={() => { setPending(null); setError(null); }}>Use a different card</button>
            ) : (
              <button type="button" className={secondaryButtonClass} disabled={busy} onClick={() => { setOpen(false); setError(null); setResult(null); }}>Cancel</button>
            )}
            <button type="submit" className={`${primaryButtonClass} text-base`} disabled={busy || (!pending && (!!amountProblem || form.status !== 'ready'))}>
              {busy ? 'Paying…' : pending ? 'Try the same payment again' : `Pay ${amountProblem ? '' : formatUsd(amount)}`.trim()}
            </button>
          </div>
          <p className="text-xs text-gray-500">Card details go directly to our card processor; we never see or store your card number.</p>
        </form>
      )}
    </section>
  );
}
